import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Badge from '../components/Badge';
import '../index.css';

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRarity, setSelectedRarity] = useState('all');

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get('/api/achievements/my-achievements', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAchievements(response.data.data.achievements);
      setStats(response.data.data.stats);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setError('Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  const categories = {
    all: 'All Achievements',
    'getting-started': '🚀 Getting Started',
    social: '👥 Social',
    giveaways: '🎁 Giveaways',
    trading: '🤝 Trading',
    engagement: '💬 Engagement',
    content: '🎨 Content',
    activity: '⚡ Activity',
    longevity: '📅 Longevity',
    'indie-dev': '🎮 Indie Dev',
    seasonal: '🎃 Seasonal',
    special: '✨ Special'
  };

  const rarities = {
    all: 'All Rarities',
    common: 'Common',
    rare: 'Rare',
    epic: 'Epic',
    legendary: 'Legendary'
  };

  const filteredAchievements = achievements.filter((achievement) => {
    const categoryMatch = selectedCategory === 'all' || achievement.category === selectedCategory;
    const rarityMatch = selectedRarity === 'all' || achievement.rarity === selectedRarity;
    return categoryMatch && rarityMatch;
  });

  const unlockedCount = filteredAchievements.filter(a => a.unlocked).length;

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text)' }}>
          <div className="loading-spinner" />
          Loading achievements...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--error)' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 20px',
        borderRadius: '12px',
        marginBottom: '30px',
        textAlign: 'center',
        color: '#fff'
      }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '36px', fontWeight: 'bold' }}>
          🏆 Achievements
        </h1>
        <p style={{ margin: '0 0 20px 0', fontSize: '16px', opacity: 0.9 }}>
          Track your progress and unlock exclusive badges
        </p>
        
        {/* Overall Progress */}
        {stats && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '30px',
            flexWrap: 'wrap',
            marginTop: '20px'
          }}>
            <div style={statBoxStyle}>
              <div style={statNumberStyle}>{stats.unlocked}</div>
              <div style={statLabelStyle}>Unlocked</div>
            </div>
            <div style={statBoxStyle}>
              <div style={statNumberStyle}>{stats.total}</div>
              <div style={statLabelStyle}>Total</div>
            </div>
            <div style={statBoxStyle}>
              <div style={statNumberStyle}>{stats.completionPercentage}%</div>
              <div style={statLabelStyle}>Complete</div>
            </div>
          </div>
        )}
      </div>

      {/* Filter Section */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '20px',
        borderRadius: '12px',
        marginBottom: '30px',
        border: '1px solid var(--border)'
      }}>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
          alignItems: 'center'
        }}>
          {/* Category Filter */}
          <div style={{ flex: '1 1 300px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={selectStyle}
            >
              {Object.entries(categories).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Rarity Filter */}
          <div style={{ flex: '1 1 300px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>
              Rarity
            </label>
            <select
              value={selectedRarity}
              onChange={(e) => setSelectedRarity(e.target.value)}
              style={selectStyle}
            >
              {Object.entries(rarities).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          {/* Filter Results */}
          <div style={{ flex: '1 1 200px', textAlign: 'right', color: 'var(--text-secondary)' }}>
            Showing {unlockedCount} / {filteredAchievements.length} unlocked
          </div>
        </div>
      </div>

      {/* Achievements Grid */}
      <div style={{
        background: 'var(--card-bg)',
        padding: '30px',
        borderRadius: '12px',
        border: '1px solid var(--border)'
      }}>
        {filteredAchievements.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
            <p>No achievements found for the selected filters.</p>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '20px',
            justifyItems: 'center'
          }}>
            {filteredAchievements.map((achievement) => (
              <div key={achievement.id} style={{ position: 'relative' }}>
                <Badge
                  achievement={achievement}
                  size="large"
                  showProgress={true}
                  unlocked={achievement.unlocked}
                />
                
                {/* Achievement Details on Hover */}
                <div style={{
                  position: 'absolute',
                  bottom: '-60px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0,0,0,0.9)',
                  color: '#fff',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  zIndex: 10,
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  textAlign: 'center'
                }}
                className="achievement-tooltip"
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{achievement.name}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>{achievement.description}</div>
                  {achievement.points > 0 && (
                    <div style={{ marginTop: '4px', color: '#f39c12' }}>+{achievement.points} points</div>
                  )}
                  {!achievement.unlocked && achievement.progress && (
                    <div style={{ marginTop: '6px', fontSize: '11px' }}>
                      Progress: {achievement.progress.current} / {achievement.progress.target}
                    </div>
                  )}
                  {achievement.unlocked && achievement.unlockedAt && (
                    <div style={{ marginTop: '6px', fontSize: '10px', opacity: 0.6 }}>
                      Unlocked: {new Date(achievement.unlockedAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .achievement-tooltip:hover {
          opacity: 1 !important;
        }
        
        @media (max-width: 768px) {
          .page-container {
            padding: 15px;
          }
        }
      `}</style>
    </div>
  );
};

// Styles
const statBoxStyle = {
  background: 'rgba(255,255,255,0.1)',
  padding: '15px 30px',
  borderRadius: '8px',
  backdropFilter: 'blur(10px)'
};

const statNumberStyle = {
  fontSize: '32px',
  fontWeight: 'bold',
  marginBottom: '4px'
};

const statLabelStyle = {
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  opacity: 0.9
};

const selectStyle = {
  width: '100%',
  padding: '10px 15px',
  borderRadius: '8px',
  border: '1px solid var(--border)',
  background: 'var(--input-bg)',
  color: 'var(--text)',
  fontSize: '14px',
  cursor: 'pointer',
  outline: 'none'
};

export default Achievements;
